import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { queryKeyFactory } from 'utils/query-client';
import { ShopItemsByBenefitFilters, ShopItemsFilters } from './shop.types';
import { loaders } from './shop.loaders';
import { converters } from './shop.converters';

export const shopKeys = queryKeyFactory('shop');

export const useShopItems = (filters: Partial<ShopItemsFilters> = {}) => {
  return useQuery({
    queryKey: shopKeys.list(filters),
    queryFn: () => loaders.getShopItems({ filters }),
    select: (data) => converters.getShopItems(data, filters.isSelected),
  });
};

export const useShopItemsByBenefit = (
  benefitId: string,
  filters: Partial<ShopItemsByBenefitFilters> = {},
) => {
  return useQuery({
    queryKey: shopKeys.list(benefitId, filters),
    queryFn: () =>
      loaders.getShopItemsByBenefit({
        benefitId,
        filters,
      }),
    select: converters.getShopItemsByBenefit,
    enabled: !!benefitId,
  });
};

export const useInfiniteShopItems = (
  filters: Partial<ShopItemsFilters> = {},
) => {
  return useInfiniteQuery(
    shopKeys.list('infinite', filters),
    ({ pageParam = 1 }) =>
      loaders.getShopItems({
        filters: { ...filters, page: pageParam },
      }),
    {
      getNextPageParam: ({ data }, allPages) => {
        const totalPages = Math.ceil(data.count / data.pageSize);
        const currentPage = allPages.length;

        return currentPage < totalPages ? currentPage + 1 : undefined;
      },
      select: ({ pages, pageParams }) => ({
        pages: pages.map((page) =>
          converters.getShopItems(page, filters.isSelected),
        ),
        pageParams,
      }),
    },
  );
};

export const useInfiniteShopItemsByBenefit = (
  benefitId: string,
  filters: Partial<ShopItemsByBenefitFilters> = {},
) => {
  return useInfiniteQuery(
    shopKeys.list(benefitId, 'infinite', filters),
    ({ pageParam = 1 }) =>
      loaders.getShopItemsByBenefit({
        benefitId,
        filters: { ...filters, page: pageParam },
      }),
    {
      getNextPageParam: ({ data }, allPages) => {
        const totalPages = Math.ceil(data.count / data.pageSize);
        const currentPage = allPages.length;

        return currentPage < totalPages ? currentPage + 1 : undefined;
      },
      select: ({ pages, pageParams }) => ({
        pages: pages.map((page) => converters.getShopItemsByBenefit(page)),
        pageParams,
      }),
      enabled: !!benefitId,
    },
  );
};
